import React from 'react';
import { HomeModernIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { AccessibilityFeatures as AccessibilityFeaturesType } from '../../components/property/property-card/PropertyCardTypes';

interface AccessibilityFeaturesProps {
  accessibility?: AccessibilityFeaturesType;
}

/**
 * AccessibilityFeatures Component
 *
 * Displays property accessibility information in compliance with the European Accessibility Act (EAA).
 * EAA Article 4 requires platforms to display accessibility information for products/services being sold.
 */
const AccessibilityFeatures: React.FC<AccessibilityFeaturesProps> = ({
  accessibility
}) => {
  if (!accessibility) return null;

  const features = [
    {
      key: 'hasStepFreeEntry',
      label: 'Step-free Entry',
      enabled: accessibility.hasStepFreeEntry
    },
    {
      key: 'hasElevatorAccess',
      label: 'Elevator Access',
      enabled: accessibility.hasElevatorAccess
    },
    {
      key: 'hasRollInShower',
      label: 'Roll-in Shower',
      enabled: accessibility.hasRollInShower
    },
    {
      key: 'wideDoorways',
      label: 'Wide Doorways',
      enabled: accessibility.wideDoorways
    },
    {
      key: 'visualFireAlarm',
      label: 'Visual Fire Alarm',
      enabled: accessibility.visualFireAlarm
    }
  ];

  const enabledFeatures = features.filter((f) => f.enabled);

  if (enabledFeatures.length === 0) return null;

  return (
    <section
      id="accessibility"
      className="bg-white p-6 rounded-lg shadow-lg"
      aria-labelledby="accessibility-heading"
    >
      <h2
        id="accessibility-heading"
        className="text-3xl font-bold mb-3 text-gray-900 flex items-center"
      >
        <HomeModernIcon
          className="w-5 h-5 mr-2 text-blue-500"
          aria-hidden="true"
        />
        Accessibility Features
      </h2>

      <ul className="space-y-2">
        {enabledFeatures.map((feature) => (
          <li key={feature.key} className="flex items-center text-gray-700">
            <CheckCircleIcon
              className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
              aria-hidden="true"
            />
            <span className="text-sm">{feature.label}</span>
          </li>
        ))}
      </ul>

      <p className="text-xs text-gray-500 mt-4 pt-3 border-t border-gray-200">
        Information provided by property owner (EAA compliance). Contact
        landlord for details.
      </p>
    </section>
  );
};

export default AccessibilityFeatures;
